// Agent collections — types + React Query hooks (DEV-16997).
//
// Wraps the /v1/agent-collections API (DEV-16821). A collection is a named
// GROUP OF AGENTS; a memory/skill bound to a collection applies to every
// member agent (resolved at read time — "sugar over bindings"). Collections
// are agent groups and do NOT apply to workflow llm-agent nodes.
//
//   GET    /v1/agent-collections            — list (summaries; ?agentId= filters to an agent's)
//   GET    /v1/agent-collections/:id        — detail (member + bound memory/skill ids)
//   POST   /v1/agent-collections            — create (name + optional member agentIds)
//   PATCH  /v1/agent-collections/:id        — rename
//   DELETE /v1/agent-collections/:id        — soft-delete
//   POST   /v1/agent-collections/:id/members            — add member agents
//   DELETE /v1/agent-collections/:id/members/:agentId   — remove a member
//   POST   /v1/agent-collections/:id/memory-bindings    — bind memories
//   DELETE /v1/agent-collections/:id/memory-bindings/:memoryId
//   POST   /v1/agent-collections/:id/skill-bindings     — bind skills
//   DELETE /v1/agent-collections/:id/skill-bindings/:skillId

import { useClient } from '@cortex/react-query';
import type {
  AddCollectionMembersInput,
  AgentCollectionDetail,
  AgentCollectionSummary,
  BindCollectionMemoriesInput,
  BindCollectionSkillsInput,
  CreateAgentCollectionInput,
} from '@cortex/types';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

export type { AgentCollectionDetail, AgentCollectionSummary, CreateAgentCollectionInput };

interface IPaginatedEnvelope<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

const collectionListKey = ['agent-collections'] as const;
const collectionDetailKey = (id: string) => [...collectionListKey, id] as const;

// Collection membership/bindings change what memories/skills resolve for the
// member agents — coarse keys, so the resolved/list/detail variants (which
// nest under them in lib/agent-memory.ts and lib/agent-skill.ts) refetch too.
const memoryListKey = ['memories'] as const;
const skillListKey = ['skills'] as const;

/** List collections (summaries). `agentId` filters to collections that agent is a member of. */
export function useCollectionList(agentId?: string) {
  const client = useClient();
  return useQuery({
    queryKey: agentId ? ([...collectionListKey, { agentId }] as const) : collectionListKey,
    queryFn: () =>
      client.http
        .get('v1/agent-collections', {
          searchParams: { limit: '200', ...(agentId ? { agentId } : {}) },
        })
        .json<IPaginatedEnvelope<AgentCollectionSummary>>()
        .then((page) => page.items),
  });
}

export function useCollection(id: string | null) {
  const client = useClient();
  return useQuery({
    queryKey: collectionDetailKey(id ?? ''),
    queryFn: () => client.http.get(`v1/agent-collections/${id}`).json<AgentCollectionDetail>(),
    enabled: Boolean(id),
  });
}

export function useCreateCollection() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAgentCollectionInput) =>
      client.http.post('v1/agent-collections', { json: body }).json<AgentCollectionDetail>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: collectionListKey });
    },
  });
}

export function useRenameCollection() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      client.http
        .patch(`v1/agent-collections/${id}`, { json: { name } })
        .json<AgentCollectionDetail>(),
    onSuccess: async (_data, { id }) => {
      await qc.invalidateQueries({ queryKey: collectionListKey });
      await qc.invalidateQueries({ queryKey: collectionDetailKey(id) });
    },
  });
}

export function useDeleteCollection() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/agent-collections/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: collectionListKey });
      // Deleting a collection un-derives its memories/skills from members.
      await qc.invalidateQueries({ queryKey: memoryListKey });
      await qc.invalidateQueries({ queryKey: skillListKey });
    },
  });
}

/** A mutation that targets one collection and refreshes its detail + the list. */
function useCollectionChildMutation<TArgs>(
  fn: (
    client: ReturnType<typeof useClient>,
    id: string,
    args: TArgs,
  ) => Promise<AgentCollectionDetail>,
) {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, args }: { id: string; args: TArgs }) => fn(client, id, args),
    onSuccess: async (_data, { id }) => {
      await qc.invalidateQueries({ queryKey: collectionListKey });
      await qc.invalidateQueries({ queryKey: collectionDetailKey(id) });
      // Membership/binding edits change what member agents resolve — refresh
      // the memory/skill families (lists + resolved views) too.
      await qc.invalidateQueries({ queryKey: memoryListKey });
      await qc.invalidateQueries({ queryKey: skillListKey });
    },
  });
}

/**
 * Which collections contain each of the given agents — one cached query per
 * agent (same key/fetch as `useCollectionList(agentId)`), returned as a
 * Map<agentId, Set<collectionId>>. Used to expand collection membership
 * client-side so list filters can honor collection-derived bindings.
 */
export function useCollectionIdsByAgent(agentIds: string[]): Map<string, Set<string>> {
  const client = useClient();
  const results = useQueries({
    queries: agentIds.map((agentId) => ({
      queryKey: [...collectionListKey, { agentId }] as const,
      queryFn: () =>
        client.http
          .get('v1/agent-collections', { searchParams: { limit: '200', agentId } })
          .json<IPaginatedEnvelope<AgentCollectionSummary>>()
          .then((page) => page.items),
    })),
  });
  const joined = agentIds
    .map((agentId, i) => `${agentId}:${(results[i]?.data ?? []).map((c) => c.id).join(',')}`)
    .join('|');
  return useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const entry of joined.split('|')) {
      if (!entry) continue;
      const [agentId, ids] = entry.split(':');
      if (agentId) map.set(agentId, new Set(ids ? ids.split(',') : []));
    }
    return map;
  }, [joined]);
}

/**
 * Does a memory/skill row reach ANY of the given agents? Mirrors the server
 * resolver's semantics: appliesToAll ∪ direct agent binding ∪ via a
 * collection the agent belongs to (`collectionsByAgent` from
 * `useCollectionIdsByAgent`). Keeps list filters consistent with the
 * resolved counts on the agent-detail page.
 */
export function bindingReachesAnyAgent(
  row: { appliesToAll: boolean; agentIds: string[]; collectionIds: string[] },
  agentIds: string[],
  collectionsByAgent: Map<string, Set<string>>,
): boolean {
  if (row.appliesToAll) return true;
  return agentIds.some(
    (agentId) =>
      row.agentIds.includes(agentId) ||
      row.collectionIds.some((cid) => collectionsByAgent.get(agentId)?.has(cid) ?? false),
  );
}

export function useAddCollectionMembers() {
  return useCollectionChildMutation<AddCollectionMembersInput>((client, id, args) =>
    client.http
      .post(`v1/agent-collections/${id}/members`, { json: args })
      .json<AgentCollectionDetail>(),
  );
}

export function useRemoveCollectionMember() {
  return useCollectionChildMutation<{ agentId: string }>((client, id, { agentId }) =>
    client.http
      .delete(`v1/agent-collections/${id}/members/${agentId}`)
      .json<AgentCollectionDetail>(),
  );
}

export function useBindCollectionMemories() {
  return useCollectionChildMutation<BindCollectionMemoriesInput>((client, id, args) =>
    client.http
      .post(`v1/agent-collections/${id}/memory-bindings`, { json: args })
      .json<AgentCollectionDetail>(),
  );
}

export function useUnbindCollectionMemory() {
  return useCollectionChildMutation<{ memoryId: string }>((client, id, { memoryId }) =>
    client.http
      .delete(`v1/agent-collections/${id}/memory-bindings/${memoryId}`)
      .json<AgentCollectionDetail>(),
  );
}

export function useBindCollectionSkills() {
  return useCollectionChildMutation<BindCollectionSkillsInput>((client, id, args) =>
    client.http
      .post(`v1/agent-collections/${id}/skill-bindings`, { json: args })
      .json<AgentCollectionDetail>(),
  );
}

export function useUnbindCollectionSkill() {
  return useCollectionChildMutation<{ skillId: string }>((client, id, { skillId }) =>
    client.http
      .delete(`v1/agent-collections/${id}/skill-bindings/${skillId}`)
      .json<AgentCollectionDetail>(),
  );
}
